import { useCallback, useEffect, useRef, useState } from 'react';
import { useMode } from '@/providers/ModeProvider';
import type { FilterPreferenceResponse, SaveFilterPreferenceApiResponse } from '@/types/filter-preference';
import { toSavedFilterPreset } from '@/types/filter-preference';
import type { SavedFilterPreset } from '@/types/transaction';
import { useApi } from './useApi';

const STORAGE_KEY_PREFIX = 'filter-preferences';

export const DEFAULT_OUTSTANDING_PRESET_ID = '__default_outstanding__';

export const OUTSTANDING_TRANSACTIONS_PRESET: SavedFilterPreset = {
  id: DEFAULT_OUTSTANDING_PRESET_ID,
  name: 'Outstanding Transactions',
  filters: {
    customerIds: [],
    transactionTypes: [],
    statuses: [],
    currencies: [],
    searchQuery: '',
    outstandingBalanceNotZero: true,
  },
  isDefault: true,
};

interface UseFilterPreferencesOptions<T> {
  defaultPresets?: SavedFilterPreset<T>[];
}

export function filtersMatch<T>(a: T, b: T): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function loadPresetsFromLocalStorage<T>(screenName: string): SavedFilterPreset<T>[] {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}-${screenName}`);
    if (stored) {
      return JSON.parse(stored) as SavedFilterPreset<T>[];
    }
  } catch {
    console.warn(`Failed to load filter preferences from localStorage for screen: ${screenName}`);
  }
  return [];
}

function savePresetsToLocalStorage<T>(screenName: string, presets: SavedFilterPreset<T>[]): void {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}-${screenName}`, JSON.stringify(presets));
  } catch {
    console.warn('Failed to cache filter preferences to localStorage');
  }
}

export function useFilterPreferences<T>(screenName: string, options?: UseFilterPreferencesOptions<T>) {
  const { defaultPresets = [] } = options ?? {};

  const [savedPresets, setSavedPresets] = useState<SavedFilterPreset<T>[]>(() =>
    loadPresetsFromLocalStorage<T>(screenName),
  );
  const [activePresetId, setActivePresetId] = useState<string | null>(
    defaultPresets.length > 0 ? defaultPresets[0].id : null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const { apiGet, apiPost, apiDelete } = useApi();
  const { isDemo } = useMode();
  const apiLoadedRef = useRef(false);

  // Load presets from API on mount
  useEffect(() => {
    if (isDemo) {
      setIsLoading(false);
      return;
    }

    if (apiLoadedRef.current) return;
    let cancelled = false;

    async function loadFromApi() {
      try {
        const response = await apiGet<FilterPreferenceResponse[]>('/api/v1/filterPreferences', { screenName });
        if (cancelled) return;

        const presets = response.map((r) => toSavedFilterPreset<T>(r));
        setSavedPresets(presets);
        savePresetsToLocalStorage<T>(screenName, presets);
        apiLoadedRef.current = true;
      } catch {
        // Will use localStorage fallback; retry when apiGet changes
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadFromApi();
    return () => {
      cancelled = true;
    };
  }, [apiGet, screenName, isDemo]);

  const savePreset = useCallback(
    async (presetName: string, filters: T): Promise<string> => {
      const filterJson = JSON.stringify(filters);

      const isDuplicate = savedPresets.some((x) => x.name.toUpperCase() === presetName.toUpperCase());
      if (isDuplicate) {
        throw new Error(`A preset named '${presetName}' already exists`);
      }

      if (isDemo) {
        const localPreset: SavedFilterPreset<T> = {
          id: `preset-${Date.now()}`,
          name: presetName,
          filters,
          isDefault: false,
        };
        setSavedPresets((prev) => {
          const updated = [...prev, localPreset];
          savePresetsToLocalStorage<T>(screenName, updated);
          return updated;
        });
        return localPreset.id;
      }

      const response = await apiPost<SaveFilterPreferenceApiResponse>('/api/v1/filterPreferences', {
        screenName,
        presetName,
        filterJson,
      });

      const newPreset: SavedFilterPreset<T> = {
        id: response.rowKey,
        name: presetName,
        filters,
        isDefault: false,
      };

      setSavedPresets((prev) => {
        const updated = [...prev, newPreset];
        savePresetsToLocalStorage<T>(screenName, updated);
        return updated;
      });

      return newPreset.id;
    },
    [apiPost, screenName, isDemo, savedPresets],
  );

  const deletePreset = useCallback(
    async (presetId: string): Promise<void> => {
      if (!isDemo) {
        await apiDelete(`/api/v1/filterPreferences/${encodeURIComponent(presetId)}`);
      }

      setSavedPresets((prev) => {
        const updated = prev.filter((x) => x.id !== presetId);
        savePresetsToLocalStorage<T>(screenName, updated);
        return updated;
      });

      setActivePresetId((prev) => (prev === presetId ? null : prev));
    },
    [apiDelete, screenName, isDemo],
  );

  const applyPreset = useCallback((preset: SavedFilterPreset<T>): T => {
    setActivePresetId(preset.id);
    return preset.filters;
  }, []);

  const clearActivePreset = useCallback(() => {
    setActivePresetId(null);
  }, []);

  const allPresets = [...defaultPresets, ...savedPresets];

  return {
    savedPresets: allPresets,
    activePresetId,
    isLoading,
    savePreset,
    deletePreset,
    applyPreset,
    clearActivePreset,
    setActivePresetId,
  };
}
